import { Button, InputBase, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useQueryClient } from '@tanstack/react-query';
import { classNames, Select, sendTrackingEvent, toastNotifications, useOidcContext } from '@uag/react-core';
import { clsx } from 'clsx';
import { zod4Resolver as zodResolver } from 'mantine-form-zod-resolver';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { default as PhoneInput } from 'react-phone-number-input/input';
import { z } from 'zod';

import { DetailUserModel, Salutation, TenantLoginProviderType, UpdateUserModel } from 'api/v3/models';
import { getGetCurrentUserDetailsQueryKey, useGetSupportedCultureCodes, useUpdateCurrentUser } from 'api/v3/user/user';
import { resources, TranslationKeys } from 'i18n';
import { HotjarEvents } from 'utils/hotjar';
import { userPhoneTextRegex, userProfileTextRegex } from 'utils/userFieldRegex';

type ProfileFormProps = {
    user: DetailUserModel;
    className?: string;
};

const userSchema = z.object({
    title: z.string().regex(userProfileTextRegex, 'invalidProfileText').optional(),
    firstName: z.string().min(1, 'isRequired').regex(userProfileTextRegex, 'invalidProfileText'),
    lastName: z.string().min(1, 'isRequired').regex(userProfileTextRegex, 'invalidProfileText'),
    phoneNumber: z.string().regex(userPhoneTextRegex, 'invalidPhoneNumber').optional(),
    mobileNumber: z.string().regex(userPhoneTextRegex, 'invalidPhoneNumber').optional(),
    email: z.string().readonly(),
    userName: z.string().readonly(),
    language: z.string().optional(),
});

type UserSchema = z.infer<typeof userSchema>;

const getDefaultValue = (user: DetailUserModel): UserSchema => ({
    title: user.title ?? '',
    firstName: user.firstName ?? '',
    lastName: user.lastName ?? '',
    phoneNumber: user.phoneNumber ?? '',
    mobileNumber: user.mobileNumber ?? '',
    email: user.email ?? '',
    userName: user.userName ?? '',
    language: user.language ?? '',
});

export const ProfileForm = ({ user, className }: ProfileFormProps) => {
    const { t } = useTranslation();
    const [previousUser, setPreviousUser] = useState(user);
    const { reloadUser } = useOidcContext();

    const queryClient = useQueryClient();
    const { data: supportedLanguages, isFetched: isSupportedLanguagesFetched } = useGetSupportedCultureCodes();
    const { mutateAsync: updateCurrentUser, isPending: isUpdateUserLoading } = useUpdateCurrentUser();
    const { getInputProps, setValues, onSubmit, errors, isDirty, isValid, resetDirty } = useForm<UserSchema>({
        mode: 'controlled',
        validate: zodResolver(userSchema),
        validateInputOnChange: true,
        initialValues: getDefaultValue(user),
    });

    if (user !== previousUser) {
        setValues(getDefaultValue(user));
        resetDirty();
        setPreviousUser(user);
    }

    const handleSave = async (values: UserSchema) => {
        const data: UpdateUserModel = {
            ...values,
            salutation: Salutation.Undefined,
        };
        try {
            await updateCurrentUser({ data });
            sendTrackingEvent(HotjarEvents.ProfileChanged);
            resetDirty();
            queryClient.invalidateQueries({ queryKey: getGetCurrentUserDetailsQueryKey() });
            reloadUser(); // Reload oidc user to update the language in the UserLanguageProvider (react-core)
            toastNotifications.success({
                title: t('success'),
                message: t('dataSaved', { data: t('accountInformation') }),
            });
        } catch {
            toastNotifications.error({
                title: t('failed'),
                message: t('dataNotSaved', { data: t('accountInformation') }),
            });
        }
    };

    const isActiveDirectoryUser = user.loginProviderType === TenantLoginProviderType.AzureAD;
    const fieldError = (key: keyof UserSchema) =>
        errors[key] ? t(errors[key].toString() as TranslationKeys) : undefined;

    return (
        <>
            <div className="flex flex-col gap-1">
                <h6 className="text-xl">{t('personalInformation')}</h6>
                <p className="text-text-light">{t('personalInformationMessage')}</p>
            </div>

            <form className={clsx(className, 'flex flex-col gap-4')} onSubmit={onSubmit(handleSave)}>
                <div className="w-[calc(50%-1rem)]">
                    <TextInput
                        className="w-full"
                        data-testid="title"
                        label={t('title')}
                        {...getInputProps('title')}
                        error={fieldError('title')}
                    />
                </div>

                <div className="flex flex-col gap-4">
                    <div className="gap-base flex flex-col">
                        {!isActiveDirectoryUser && (
                            <div className="flex gap-8">
                                <TextInput
                                    className="w-1/2"
                                    data-testid="firstName"
                                    label={t('firstName')}
                                    {...getInputProps('firstName')}
                                    error={fieldError('firstName')}
                                    required
                                />
                                <TextInput
                                    className="w-1/2"
                                    data-testid="lastName"
                                    label={t('lastName')}
                                    {...getInputProps('lastName')}
                                    error={fieldError('lastName')}
                                    required
                                />
                            </div>
                        )}
                        <div className="flex gap-8">
                            {!user.phoneNumberSynced && (
                                <InputBase
                                    className="w-1/2"
                                    classNames={classNames.textField.default}
                                    component={PhoneInput}
                                    data-testid="phoneNumber"
                                    label={t('phoneNumber')}
                                    {...getInputProps('phoneNumber')}
                                    error={fieldError('phoneNumber')}
                                />
                            )}
                            {!user.mobileNumberSynced && (
                                <InputBase
                                    className="w-1/2"
                                    classNames={classNames.textField.default}
                                    component={PhoneInput}
                                    data-testid="mobileNumber"
                                    label={t('mobileNumber')}
                                    {...getInputProps('mobileNumber')}
                                    error={fieldError('mobileNumber')}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <div className="w-[calc(50%-1rem)]">
                    <div className="flex flex-col gap-1">
                        <h6 className="text-xl">{t('language')}</h6>
                        <p className="text-text-light">{t('languageMessage')}</p>
                    </div>
                    <Select
                        className="w-full"
                        data={Object.keys(resources)
                            .filter((code) => supportedLanguages?.data.includes(code))
                            .map((code) => ({
                                value: code,
                                label: resources[code as keyof typeof resources].Name,
                            }))}
                        data-testid="language"
                        disabled={!isSupportedLanguagesFetched}
                        label={t('default')}
                        searchable
                        {...getInputProps('language')}
                    />
                </div>

                <Button
                    className="xs:col-start-1 mr-auto"
                    data-testid="save"
                    disabled={!isDirty() || !isValid()}
                    loading={isUpdateUserLoading}
                    type="submit"
                >
                    {t('saveProfile')}
                </Button>
            </form>
        </>
    );
};
